import React, { useState, useMemo } from 'react';
import Layout from 'layout/Layout';
import { useGetHealthCheckerQuizQuery } from 'api/healthCheckerApi';

const getHealthStatus = (score) => {
    if (score >= 8) {
        return {
            label: 'Excellent',
            color: '#065f46', // text-green-800
            bgColor: '#d1fae5', // bg-green-100
        };
    } else if (score >= 6) {
        return {
            label: 'Good',
            color: '#92400e', // text-yellow-800
            bgColor: '#fef3c7', // bg-yellow-100
        };
    } else {
        return {
            label: 'Needs Change',
            color: '#991b1b', // text-red-800
            bgColor: '#fee2e2', // bg-red-100
        };
    }
};

const formatDateTime = (timestamp) => {
    const date = new Date(timestamp);
    const datePart = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
    const timePart = date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
    });
    return `${datePart} · ${timePart}`;
};

const HealthChecker = () => {
    const { data, isLoading, isError } = useGetHealthCheckerQuizQuery();
    const records = data?.data || [];

    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('All');

    const handleSort = (key) => {
        setSortConfig((prev) => {
            if (prev.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'asc' };
        });
    };

    const processedRecords = useMemo(() => {
        // First filter
        const filtered = records.filter((quiz) => {
            const matchesSearch = 
                quiz.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                quiz.mobile_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                quiz.address?.toLowerCase().includes(searchTerm.toLowerCase());
            
            const statusLabel = getHealthStatus(quiz.marks).label;
            const matchesFilter = filterStatus === 'All' ? true : statusLabel === filterStatus;

            return matchesSearch && matchesFilter;
        });

        // Then sort
        if (!sortConfig.key) return filtered;

        return [...filtered].sort((a, b) => {
            let valA, valB;

            if (sortConfig.key === 'full_name') {
                valA = a.full_name.toLowerCase();
                valB = b.full_name.toLowerCase();
            } else if (sortConfig.key === 'marks') {
                valA = a.marks;
                valB = b.marks;
            } else if (sortConfig.key === 'created_at') {
                valA = new Date(a.created_at).getTime();
                valB = new Date(b.created_at).getTime();
            }

            if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
            if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    }, [records, sortConfig, searchTerm, filterStatus]);

    const renderSortIndicator = (key) => {
        if (sortConfig.key !== key) return (
            <svg className="w-4 h-4 text-gray-400 inline-block ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"></path></svg>
        );
        return sortConfig.direction === 'asc' ? (
            <svg className="w-4 h-4 text-blue-600 inline-block ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7"></path></svg>
        ) : (
            <svg className="w-4 h-4 text-blue-600 inline-block ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
        );
    };

    return (
        <Layout title="Healthchecker Quiz" tagline="Manage and filter Healthchecker Quiz submissions">
            <div className="max-w-7xl mx-auto mt-10 md:mt-16 px-4 sm:px-6 lg:px-8 mb-20">
                
                {/* Header & Controls Section */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 mb-6 flex flex-col md:flex-row justify-between items-center gap-4">
                    <div className="flex-1 w-full relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <input
                            type="text"
                            placeholder="Search by name, contact, or address..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
                        />
                    </div>
                    
                    <div className="flex items-center gap-3 w-full md:w-auto">
                        <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Filter Status:</label>
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="border border-gray-300 rounded-lg px-4 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[150px]"
                        >
                            <option value="All">All Statuses</option>
                            <option value="Excellent">Excellent (8+)</option>
                            <option value="Good">Good (6-7)</option>
                            <option value="Needs Change">Needs Change (&lt;6)</option>
                        </select>
                    </div>
                </div>

                {/* Table Section */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    {isLoading && (
                        <div className="flex justify-center items-center h-40">
                            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600"></div>
                        </div>
                    )}
                    
                    {isError && (
                        <div className="p-8 text-center text-red-500">
                            <p className="font-semibold">Error fetching records.</p>
                            <p className="text-sm">Please try refreshing the page.</p>
                        </div>
                    )}

                    {!isLoading && !isError && (
                        <div className="overflow-x-auto">
                            <table className="w-full whitespace-nowrap text-left text-sm text-gray-600">
                                <thead className="bg-gray-50 text-gray-800 uppercase font-semibold text-xs border-b border-gray-200">
                                    <tr>
                                        <th 
                                            className="px-6 py-4 cursor-pointer hover:bg-gray-100 transition-colors select-none group"
                                            onClick={() => handleSort('full_name')}
                                        >
                                            <div className="flex items-center">
                                                Full Name {renderSortIndicator('full_name')}
                                            </div>
                                        </th>
                                        <th className="px-6 py-4">Mobile Number</th>
                                        <th className="px-6 py-4">Address</th>
                                        <th 
                                            className="px-6 py-4 text-center cursor-pointer hover:bg-gray-100 transition-colors select-none group"
                                            onClick={() => handleSort('marks')}
                                        >
                                            <div className="flex items-center justify-center">
                                                Score {renderSortIndicator('marks')}
                                            </div>
                                        </th>
                                        <th 
                                            className="px-6 py-4 cursor-pointer hover:bg-gray-100 transition-colors select-none group"
                                            onClick={() => handleSort('created_at')}
                                        >
                                            <div className="flex items-center">
                                                Submitted At {renderSortIndicator('created_at')}
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {processedRecords.length > 0 ? (
                                        processedRecords.map((quiz) => {
                                            const status = getHealthStatus(quiz.marks);
                                            return (
                                                <tr key={quiz.id} className="hover:bg-blue-50/50 transition-colors">
                                                    <td className="px-6 py-4 font-medium text-gray-900">{quiz.full_name}</td>
                                                    <td className="px-6 py-4">{quiz.mobile_number}</td>
                                                    <td className="px-6 py-4 max-w-xs truncate" title={quiz.address}>{quiz.address}</td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span 
                                                            className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold shadow-sm"
                                                            style={{
                                                                color: status.color,
                                                                backgroundColor: status.bgColor,
                                                            }}
                                                        >
                                                            {quiz.marks} / {quiz.out_of} ({status.label})
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-gray-500 text-xs">
                                                        {formatDateTime(quiz.created_at)}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                                                <svg className="mx-auto h-12 w-12 text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                                <p className="text-lg font-medium">No quiz records found</p>
                                                <p className="text-sm">Try adjusting your search or status filter.</p>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default HealthChecker;
